<template>
  <div
    class="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-5"
    @click.self="$emit('close')"
    @keydown.escape="$emit('close')"
  >
    <div
      class="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[95%] max-w-[1600px] h-[90vh] flex flex-col overflow-hidden"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-4 bg-gray-100 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700"
      >
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          Validation Results - JSON Diff
        </h2>
        <button
          class="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md text-sm font-medium transition-colors"
          @click="$emit('close')"
        >
          ✕ Close
        </button>
      </div>

      <!-- Diff Content -->
      <div class="flex-1 overflow-hidden flex">
        <!-- Old Pane -->
        <div
          class="flex-1 flex flex-col border-r border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          <div
            class="px-4 py-3 bg-red-50 dark:bg-red-900/30 border-b border-gray-200 dark:border-gray-700 font-semibold text-red-700 dark:text-red-300 sticky top-0"
          >
            Old
          </div>
          <div class="flex-1 overflow-auto font-mono text-xs">
            <div
              v-for="(line, index) in diffLines"
              :key="`old-${index}`"
              :class="[
                'flex border-t border-gray-100 dark:border-gray-700',
                line.type === 'delete' ? 'bg-red-50 dark:bg-red-900/20' : '',
                line.type === 'add' ? 'bg-gray-50 dark:bg-gray-800' : ''
              ]"
            >
              <span
                :class="[
                  'px-3 py-0.5 text-right min-w-[50px] select-none border-r border-gray-200 dark:border-gray-700',
                  line.type === 'delete'
                    ? 'bg-red-100 dark:bg-red-900/40 text-red-500'
                    : 'bg-gray-50 dark:bg-gray-900 text-gray-400'
                ]"
              >
                {{ line.type !== 'add' ? line.oldNum : '' }}
              </span>
              <pre
                :class="[
                  'flex-1 px-3 py-0.5 whitespace-pre overflow-x-auto',
                  line.type === 'delete'
                    ? 'text-red-800 dark:text-red-200'
                    : 'text-gray-700 dark:text-gray-300'
                ]"
                >{{ line.type !== 'add' ? line.oldLine : '' }}</pre
              >
            </div>
          </div>
        </div>

        <!-- New Pane -->
        <div class="flex-1 flex flex-col overflow-hidden">
          <div
            class="px-4 py-3 bg-green-50 dark:bg-green-900/30 border-b border-gray-200 dark:border-gray-700 font-semibold text-green-700 dark:text-green-300 sticky top-0"
          >
            New
          </div>
          <div class="flex-1 overflow-auto font-mono text-xs">
            <div
              v-for="(line, index) in diffLines"
              :key="`new-${index}`"
              :class="[
                'flex border-t border-gray-100 dark:border-gray-700',
                line.type === 'add' ? 'bg-green-50 dark:bg-green-900/20' : '',
                line.type === 'delete' ? 'bg-gray-50 dark:bg-gray-800' : ''
              ]"
            >
              <span
                :class="[
                  'px-3 py-0.5 text-right min-w-[50px] select-none border-r border-gray-200 dark:border-gray-700',
                  line.type === 'add'
                    ? 'bg-green-100 dark:bg-green-900/40 text-green-500'
                    : 'bg-gray-50 dark:bg-gray-900 text-gray-400'
                ]"
              >
                {{ line.type !== 'delete' ? line.newNum : '' }}
              </span>
              <pre
                :class="[
                  'flex-1 px-3 py-0.5 whitespace-pre overflow-x-auto',
                  line.type === 'add'
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-gray-700 dark:text-gray-300'
                ]"
                >{{ line.type !== 'delete' ? line.newLine : '' }}</pre
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { taskSentinelService } from '../../../services/task-sentinel'
import type { Team, DiffLine } from '../../../types/task-sentinel'

const props = defineProps<{
  oldData: Team | null
  newData: Team | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

// Computed diff
const diffLines = computed<DiffLine[]>(() => {
  const sortedOld = props.oldData ? taskSentinelService.sortJsonKeys(props.oldData) : null
  const sortedNew = props.newData ? taskSentinelService.sortJsonKeys(props.newData) : null

  const oldText = sortedOld ? JSON.stringify(sortedOld, null, 2) : ''
  const newText = sortedNew ? JSON.stringify(sortedNew, null, 2) : ''

  const oldLines = oldText.split('\n')
  const newLines = newText.split('\n')

  return taskSentinelService.computeLineDiff(oldLines, newLines)
})

// Handle escape key
const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    emit('close')
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
  // Prevent body scroll when modal is open
  document.body.style.overflow = 'hidden'
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = ''
})
</script>
